import { isServer } from "solid-js/web";

import { DefaultLocale, Locale, LocaleMap } from "@/i18n/config";
import { useLocation } from "@solidjs/router";

export const LANGUAGE_REGEX = /^\/(en|pt|es)(?:\/|$)/;

// 从 URI 中获取语言代码 不包含域名
export function getLocaleFromURI(path: string): string {
  const location = useLocation();
  const queryString = location.search;
  // 如果是根路径，直接返回默认语言
  if (path === "/" || path === "") {
    return "pt" + queryString;
  }

  const segments = path.replace(/^\/+/, "").split("/");
  const langCode = segments[0];
  const finalQueryString = queryString;

  // 检查第一个段是否是有效的语言代码
  if (langCode && Object.keys(LocaleMap).includes(langCode)) {
    // 如果是语言代码，保持完整路径
    const locale = langCode as Locale;
    // 保留语言代码后的所有路径段
    const remainingPath = segments.slice(1).join("/");
    const basePath = remainingPath
      ? `/${locale}/${remainingPath}`
      : `/${locale}`;
    if (finalQueryString) {
      return `${basePath}?${finalQueryString}`;
    }
    return basePath;
  } else {
    // 如果不是语言代码，说明是需要重定向的路径（如 /casino, /sports 等）
    // 获取用户偏好语言
    let preferredLocale = "pt";
    if (typeof document !== "undefined") {
      const cookieLocale = getLocaleFromCookie(document.cookie);
      const acceptLanguageLocale = getLocaleFromAcceptLanguage(
        navigator.language,
      );
      preferredLocale = cookieLocale || acceptLanguageLocale || "pt";
    }

    // 构建带语言前缀的完整路径
    const fullPath =
      path === "/" ? `/${preferredLocale}` : `/${preferredLocale}${path}`;

    return fullPath;
  }
}

// 从 URL 路径中获取语言代码
export function getLocaleFromPath(path: string): Locale | null {
  let langCode = "pt";
  try {
    const match = path.match(/^\/(en|hk|pt|es|ph|hi|ru|th|ur|tr)(\/|$)/);
    langCode = match?.[1] || "pt";
  } catch {
    return "pt";
  }

  if (langCode && Object.keys(LocaleMap).includes(langCode)) {
    return langCode as Locale;
  }

  return null;
}

// 从 cookie 中获取语言设置
export function getLocaleFromCookie(
  cookieHeader: string | null,
): Locale | null {
  if (!cookieHeader) return null;

  const cookies = Object.fromEntries(
    cookieHeader.split("; ").map((cookie) => {
      const [key, value] = cookie.split("=");
      return [key, value];
    }),
  );

  const lang = cookies["i18n"];
  return lang && Object.keys(LocaleMap).includes(lang)
    ? (lang as Locale)
    : null;
}

// 从 Accept-Language 头获取用户偏好语言
export function getLocaleFromAcceptLanguage(
  acceptLanguageHeader: string | null,
): Locale | null {
  if (!acceptLanguageHeader) return null;

  // 解析 Accept-Language 头，格式如: "en-US,en;q=0.9,zh-CN;q=0.8,zh;q=0.7"
  const languages = acceptLanguageHeader
    .split(",")
    .map((lang) => {
      const [code, qValue] = lang.trim().split(";");
      const quality = qValue ? parseFloat(qValue.split("=")[1]) : 1.0;
      return { code: code.toLowerCase(), quality };
    })
    .sort((a, b) => b.quality - a.quality); // 按优先级排序

  // 查找匹配的语言
  for (const { code } of languages) {
    // 首先尝试完整匹配（如 "en-us"）
    const exactMatch = Object.keys(LocaleMap).find(
      (locale) => locale.toLowerCase() === code,
    );
    if (exactMatch) {
      return exactMatch as Locale;
    }

    // 然后尝试语言代码匹配（如 "en" 匹配 "en"）
    const langCode = code.split("-")[0];
    const langMatch = Object.keys(LocaleMap).find(
      (locale) => locale.toLowerCase() === langCode,
    );
    if (langMatch) {
      return langMatch as Locale;
    }
  }

  return null;
}
// 验证pathname是否默认语言
export function isValidDefaultLocale(pathname: string): boolean {
  const lang = pathname.split("/")[1];
  return lang === DefaultLocale;
}
// 移除pathname默认语言标识
export function removePrefixLocale(
  pathname: string,
  lang: string = DefaultLocale,
): string {
  if (isValidLocalePath(pathname)) {
    return pathname.replace(`/${lang}`, "");
  }

  return pathname;
}

export function isValidLocalePath(path: string): boolean {
  if (!path) {
    return false;
  }
  const match = path.match(/^\/(en|hk|pt|es|ph|hi|ru|th|ur|tr)(\/|$)/);
  return isValidLocale(match?.[1] ?? "");
}
// 验证语言代码是否有效
export function isValidLocale(lang: string): lang is Locale {
  return Object.keys(LocaleMap).includes(lang);
}

// 更新 URL 中的语言代码
export function updateUrlLocale(lang: Locale): void {
  if (isServer) return;

  const { pathname, search, hash } = window.location;
  const urlLocale = getLocaleFromPath(pathname);

  if (urlLocale && urlLocale !== lang) {
    let pathWithoutLocale = pathname.replace(new RegExp(`^/${urlLocale}`), "");

    if (!pathWithoutLocale) pathWithoutLocale = "/";

    const newPath = `/${lang}${pathWithoutLocale}${search}${hash}`;
    console.log(
      `updateUrlLocale newPath: ${newPath} pathname: ${pathname} search: ${search} hash: ${hash}`,
    );
    window.history.replaceState({}, "", newPath);
  }
}

// 获取本地化路径
export function getLocalizedPath(path: string): string {
  if (isServer) return path;

  const getpath = (path: string) => {
    // 1. 先检查 URL 中是否已经有语言代码
    const currentPath = window.location.pathname;
    const urlLocale = getLocaleFromPath(currentPath);
    if (urlLocale) {
      // 当 path 是 "/" 时，返回 "/locale/" 以确保路由正确匹配
      if (path === "/") {
        return `/${urlLocale}/`;
      }
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return `/${urlLocale}${normalizedPath}`;
    }

    // 2. 如果没有 URL 语言代码，则使用 cookie 中的语言设置
    const cookieHeader = document.cookie;
    const cookieLang = getLocaleFromCookie(cookieHeader);
    const currentLocale = cookieLang || DefaultLocale;

    // 当 path 是 "/" 时，返回 "/locale/" 以确保路由正确匹配
    if (path === "/") {
      console.log(
        `getLocalizedPath path: ${path} currentLocale: ${currentLocale} returning: /${currentLocale}/`,
      );
      return `/${currentLocale}/`;
    }
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    console.log(
      `getLocalizedPath path: ${path} currentLocale: ${currentLocale} normalizedPath: ${normalizedPath}`,
    );
    return `/${currentLocale}${normalizedPath}`;
  };

  const localePath = getpath(path);

  if (!isValidLocalePath(location.pathname)) {
    if (isValidDefaultLocale(localePath)) {
      return removePrefixLocale(localePath);
    }
  }

  return localePath;
}

/**
 * 获取真实路径，处理可能存在的语言代码前缀
 *
 * @param path - 输入的路径字符串
 * @returns 处理后的真实路径字符串
 */
export function getRealPath(path: string): string {
  // 如果是服务器端环境，直接返回原路径
  if (isServer) return path;

  // 移除开头的斜杠，确保路径格式统一
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;

  // 分割路径并移除语言代码（如果存在）
  const parts = normalizedPath.split("/");
  const firstPart = parts[0];

  // 检查第一部分是否是语言代码
  if (isValidLocale(firstPart)) {
    return parts.slice(1).join("/");
  }

  return normalizedPath;
}

export function getPagePath(path: string, hasUrlShowLang: boolean): string {
  const realPath = getRealPath(path);
  // 对于多段路径，取第一个非空段
  const pathSegments = realPath?.split("/").filter(Boolean) || [];
  return (hasUrlShowLang ? pathSegments[1] : pathSegments[0]) || "";
}
